import React, { useMemo, useState } from "react";
import { Button, Form, message, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import { addDoc, collection } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { FIRESTORE_PRICE_LISTS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { PRICING_COUNTRIES } from "../../../../../constants/PricingConstants";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { usePriceLists } from "../../../../../data/hooks/firestore/usePriceLists";
import dayjs from "dayjs";

const { Option } = Select;

const AddCountryPriceListModal = ({ visible, onClose }) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const { email } = useAuth();
  const { priceLists } = usePriceLists();
  const [loading, setLoading] = useState(false);

  const availableCountries = useMemo(() => {
    const usedCountryCodes = priceLists.filter((pl) => pl.type === "country").map((pl) => pl.countryCode);
    return PRICING_COUNTRIES.filter((c) => !usedCountryCodes.includes(c.code));
  }, [priceLists]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const selectedCountry = PRICING_COUNTRIES.find((c) => c.code === values.countryCode);
      const countryName = selectedCountry ? i18n.t(selectedCountry.label) : values.countryCode;

      const newPriceList = {
        name: countryName,
        type: "country",
        countryCode: values.countryCode,
        customPrices: {},
        createdAt: dayjs().unix(),
        createdBy: email,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      await addDoc(
        collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRICE_LISTS_TABLE),
        newPriceList,
      );

      message.success(t("notifications.country_price_list_created"));
      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Error creating country price list:", error);
      message.error(t("notifications.error_creating_country_price_list"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t("add_country_price_list")}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("cancel")}
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {t("create")}
        </Button>,
      ]}
      width={500}
      centered
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="countryCode"
          label={t("country")}
          rules={[{ required: true, message: t("form.select_country") }]}
        >
          <Select
            placeholder={t("form.select_country")}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => option.children?.toLowerCase().includes(input.toLowerCase())}
          >
            {availableCountries.map((country) => (
              <Option key={country.code} value={country.code}>
                {i18n.t(country.label)}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddCountryPriceListModal;
